import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
    ApplicationFacade,
    DetailedRoute,
    OpenTripPlannerFacade,
    ShapeType,
    Time,
    TransportMode
} from '@ridango/components';
import Env from '@ridango/core/env';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Temporal } from '@js-temporal/polyfill';
import {
    AppStartupService,
    DisruptionMessage,
    DisruptionMessageSeverity,
    DisruptionMessageType,
    TimeUtil
} from '@ridango/core';
import { BehaviorSubject, concat, interval, of, Subject, timer } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, scan, takeUntil } from 'rxjs/operators';

const MINUTES_TO_DEPARTURE_TO_SHOW_LABEL = 0;
const MINUTES_TIME_INTERVAL_TO_UPDATE = 1;

interface InformedEntity {
    tripId?: string;
    routeId?: string | number;
    stopId?: string | number;
    tripDirection?: string | number;
}

@Component({
    selector: 'rwidget-trip-departure',
    templateUrl: './trip-departure.component.html',
    styleUrls: ['./trip-departure.component.scss']
    // changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripDepartureComponent implements OnChanges, OnInit, OnDestroy {
    @Input() route: DetailedRoute;
    @Input() time: Time;
    alerts$ = this.openTripPlannerFacade.alerts$;

    assetsPath$ = this.applicationFacade.assetsPath$;
    ShapeType = ShapeType;
    thresholdMinutes = this.env.REALTIME_THRESHOLD_MINUTES;
    TIME_FORMAT = this.env.PRIMARY_TIME_FORMAT || 'HH:mm';
    TransportMode = TransportMode;
    DisruptionMessageSeverity = DisruptionMessageSeverity;
    hasDisruptionOnRoute: boolean;
    disruptionSeverityOnRoute: DisruptionMessageSeverity = DisruptionMessageSeverity.WARNING;
    // data source
    private readonly formattedTimeIndicatorSource$ = new BehaviorSubject<string>('');
    // to use it in template with additional pipe operators, used with async pipe to don't manually write 'detectChanges' each time
    public readonly formattedTimeIndicator$ = this.formattedTimeIndicatorSource$.pipe(
        distinctUntilChanged(),
        scan((lastCorrectTime: string | null, currentTime: string) => {
            if (!this.isTimeStillCorrect()) {
                this.openTripPlannerFacade.refreshStopTime(this.time.stopId);
                return lastCorrectTime;
            }

            return currentTime;
        }, null),
        // don't use debounce first time
        concatMap((value, index) => {
            return index === 0
                ? of(value)
                : // apply a debounce time to prevent flickering. The time is first updated locally and then it gets updated from remote data(we should wait until request will be finished).
                  of(value).pipe(debounceTime(TimeUtil.MILLISECONDS_IN_SECOND * 3));
        })
    );
    private componentDestroyed$ = new Subject<void>();
    timeZone = this.appStartupService.getTimezone();
    timeZoneOffset = this.appStartupService.getTimezoneOffset;

    realTimeDeparture: Temporal.ZonedDateTime;
    scheduledTimeDeparture: Temporal.ZonedDateTime;
    isDepartureWithinThreshold: boolean;
    isShowScheduledTimeBelowRealTime: boolean;
    timepoint: boolean;
    timedDeparture: string;
    mainDisplayTime: string;
    crossedOutTime: string;

    get defaultTimeType(): 'scheduled' | 'realtime' {
        return this.time.realtime ? 'realtime' : 'scheduled';
    }

    getIsShowScheduledTimeBelowRealTime() {
        return (
            this.getMinutesToDeparture('realtime') - this.getMinutesToDeparture('scheduled') >
                this.env.EXPLORE?.SCHEDULE_TIME_THRESHOLD_LATE ||
            this.getMinutesToDeparture('realtime') - this.getMinutesToDeparture('scheduled') <
                -this.env.EXPLORE?.SCHEDULE_TIME_THRESHOLD_EARLY
        );
    }

    departureInstant(type: 'realtime' | 'scheduled') {
        const departureTimestamp =
            type === 'realtime' && this.time?.realtime ? this.time.realtimeDeparture : this.time.scheduledDeparture;
        return typeof departureTimestamp === 'string'
            ? Temporal.Instant.from(departureTimestamp).toZonedDateTimeISO(this.timeZone).round('minute')
            : Temporal.Instant.fromEpochSeconds(departureTimestamp).toZonedDateTimeISO(this.timeZone).round('minute');
    }

    constructor(
        @Inject('ENV') public env: Env,
        private datePipe: DatePipe,
        private applicationFacade: ApplicationFacade,
        public translate: TranslateService,
        private appStartupService: AppStartupService,
        private openTripPlannerFacade: OpenTripPlannerFacade
    ) {}

    ngOnInit() {
        // concat depends on the params order, don't change the order; if you think that we should use switchMap instead - please rewrite it
        concat(
            // Start updating time representation only when we reach the departure second(even if BE will send 18:53:45, instead of current 18:53:00) to prevent UI blinks.
            timer(
                TimeUtil.getSecondsLeftToTargetSecond(this.departureInstant(this.defaultTimeType).second) *
                    TimeUtil.MILLISECONDS_IN_SECOND
            ),
            interval(MINUTES_TIME_INTERVAL_TO_UPDATE * TimeUtil.MILLISECONDS_IN_MINUTE)
        )
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.updateDateRepresentation();
                this.calculateDisplayValues();
            });

        this.alerts$.pipe(takeUntil(this.componentDestroyed$)).subscribe((alerts: DisruptionMessage[]) => {
            if (!this.route || !alerts?.length) return;

            const existingIds = new Set((this.route.disruptionMessages || []).map((msg) => msg.id));

            const newAlerts = alerts.filter(
                (alert) =>
                    alert.informedEntities?.some((entity) => String(entity.routeId) === String(this.route.id)) &&
                    !existingIds.has(alert.id)
            );

            if (!this.route.disruptionMessages) {
                this.route.disruptionMessages = [];
            }

            this.route.disruptionMessages.push(...newAlerts);

            this.updateDisruptionState();
        });
    }

    // there is word 'still' because it always moves to incorrect time representation 17:05 => 5 minutes left => 1 minute left => Now => incorrect (- 1 min left)
    isTimeStillCorrect() {
        return !TimeUtil.isTimePassed(
            this.departureInstant(this.defaultTimeType),
            { amount: MINUTES_TIME_INTERVAL_TO_UPDATE, type: 'minute' },
            'floor'
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (JSON.stringify(changes.time.currentValue) !== JSON.stringify(changes.time.previousValue)) {
            this.updateDateRepresentation();

            this.realTimeDeparture = this.departureInstant('realtime');
            this.scheduledTimeDeparture = this.departureInstant('scheduled');
            this.isDepartureWithinThreshold = this.getDepartureWithinThreshold();
            this.isShowScheduledTimeBelowRealTime = this.getIsShowScheduledTimeBelowRealTime();
            this.timedDeparture = this.getFormattedDelayedDepartureTime();
            this.calculateDisplayValues();
        }
        this.updateDisruptionState();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    private updateDateRepresentation() {
        if (this.getDepartureWithinThreshold()) {
            this.formattedTimeIndicatorSource$.next(this.getFormattedMinutesToDeparture(this.defaultTimeType));
        } else {
            this.formattedTimeIndicatorSource$.next(
                this.getFormattedDepartureInstant(this.departureInstant(this.defaultTimeType))
            );
        }
    }

    durationUntilDeparture(type: 'realtime' | 'scheduled') {
        return Temporal.Now.instant()
            .toZonedDateTimeISO(this.timeZone)
            .round('minute')
            .until(this.departureInstant(type));
    }

    getMinutesToDeparture(type: 'realtime' | 'scheduled') {
        return this.durationUntilDeparture(type).round('minutes').total('minutes');
    }

    getDepartureWithinThreshold(): boolean {
        return (
            Boolean(this.thresholdMinutes) &&
            this.durationUntilDeparture(this.defaultTimeType).total('minutes') <= this.thresholdMinutes
        );
    }

    getFormattedMinutesToDeparture(type: 'realtime' | 'scheduled'): string {
        const minutes = this.getMinutesToDeparture(type);
        if (minutes === MINUTES_TO_DEPARTURE_TO_SHOW_LABEL) {
            return this.translate.instant('EXPLORE_NOW_LABEL');
        }
        return `${minutes} ${this.translate.instant('EXPLORER_SCHEDULE_MINUTE')}`;
    }

    getHasDisruptionOnRoute() {
        if (!this.env.DIRECTIONAL_ALERTS) {
            // Default is just having any on route
            return this.route.disruptionOnRoute;
        }

        if (!this.route?.disruptionMessages?.length || !this.time) {
            return false;
        }

        return this.route.disruptionMessages.some((message) => this.matchesDisruptionMessageForRoute(message));
    }

    private updateDisruptionState() {
        this.hasDisruptionOnRoute = this.getHasDisruptionOnRoute();
        this.disruptionSeverityOnRoute = this.getDisruptionSeverityOnRoute();
    }

    private getDisruptionSeverityOnRoute(): DisruptionMessageSeverity {
        return this.resolveHighestDisruptionSeverity(this.getRelevantMessagesForSeverity());
    }

    private matchesDisruptionMessageForRoute(message: DisruptionMessage): boolean {
        const informedEntities = message?.informedEntities ?? [];
        if (!this.route || !this.time || !informedEntities.length || !this.isMessageActiveForDeparture(message)) {
            return false;
        }

        const entityMatcher = this.getEntityMatcherForMessage(message);
        return Boolean(entityMatcher) && informedEntities.some((entity) => entityMatcher(entity));
    }

    private getRelevantMessagesForSeverity(): DisruptionMessage[] {
        const disruptionMessages = this.route?.disruptionMessages ?? [];

        return this.env.DIRECTIONAL_ALERTS
            ? disruptionMessages.filter((message) => this.matchesDisruptionMessageForRoute(message))
            : disruptionMessages;
    }

    private isDirectionalAlert(message: DisruptionMessage): boolean {
        return (
            message?.messageType === DisruptionMessageType.ALERT_STOP ||
            message?.messageType === DisruptionMessageType.ALERT_BUS
        );
    }

    private isGenericAlert(message: DisruptionMessage): boolean {
        return message?.messageType === DisruptionMessageType.ALERT_GENERIC;
    }

    private getEntityMatcherForMessage(message: DisruptionMessage): ((entity: InformedEntity) => boolean) | null {
        if (this.isDirectionalAlert(message)) {
            return (entity) => this.matchesDirectionalEntity(entity);
        }

        if (this.isGenericAlert(message)) {
            return (entity) => this.matchesGenericEntity(entity);
        }

        return null;
    }

    private isMessageActiveForDeparture(message: DisruptionMessage): boolean {
        const activePeriodEnd =
            message.activePeriodEnd instanceof Date ? message.activePeriodEnd.toISOString() : message.activePeriodEnd;
        const activePeriodStart =
            message.activePeriodStart instanceof Date
                ? message.activePeriodStart.toISOString()
                : message.activePeriodStart;

        const endTimeMilliseconds = Temporal.Instant.from(activePeriodEnd).epochMilliseconds;
        const startTimeMilliseconds = Temporal.Instant.from(activePeriodStart).epochMilliseconds;
        const departureEpoch = this.departureInstant(this.defaultTimeType).epochMilliseconds;

        return departureEpoch < endTimeMilliseconds && departureEpoch >= startTimeMilliseconds;
    }

    private matchesDirectionalEntity(entity: InformedEntity): boolean {
        if (entity?.tripId) {
            return entity.tripId === this.time?.tripId;
        }

        const routeIdMatch = String(entity.routeId) === String(this.route.id);
        const stopIdMatch = entity.stopId ? String(entity.stopId) === String(this.time.stopId) : true;
        const directionMatchOrNull =
            entity.tripDirection == null || String(entity.tripDirection) === String(this.time.tripDirection ?? null);

        return (routeIdMatch || stopIdMatch) && directionMatchOrNull;
    }

    private matchesGenericEntity(entity: InformedEntity): boolean {
        return String(entity.routeId) === String(this.route.id);
    }

    private resolveHighestDisruptionSeverity(messages: DisruptionMessage[]): DisruptionMessageSeverity {
        if (!messages.length) {
            return DisruptionMessageSeverity.WARNING;
        }

        const severities = new Set(messages.map((message) => message.severity || DisruptionMessageSeverity.WARNING));

        if (severities.has(DisruptionMessageSeverity.CRITICAL)) {
            return DisruptionMessageSeverity.CRITICAL;
        }

        if (severities.has(DisruptionMessageSeverity.WARNING)) {
            return DisruptionMessageSeverity.WARNING;
        }

        return DisruptionMessageSeverity.INFO;
    }

    private calculateDisplayValues() {
        const plannedTime = this.getFormattedScheduledTime();
        const prognosisTime = this.getFormattedDelayedDepartureTime();
        this.timepoint = this.time.timepoint;

        if (this.timepoint) {
            this.mainDisplayTime = this.time.realtime && this.time.departureDelay != null ? prognosisTime : plannedTime;
            this.crossedOutTime = '';
            return;
        }

        if (!this.time.realtime || this.time.departureDelay == null || this.time.departureDelay === 0) {
            this.mainDisplayTime = plannedTime;
            this.crossedOutTime = '';
            return;
        }

        const diffMinutes = Math.floor(Math.abs(this.time.departureDelay / 60));
        const threshold = this.env?.APPROXIMATE_TIME_THRESHOLD_MINUTES;

        if (this.env?.APPROXIMATE_TIME_THRESHOLD_MINUTES) {
            if (diffMinutes >= threshold) {
                this.mainDisplayTime = prognosisTime;
                this.crossedOutTime = plannedTime;
            } else {
                this.mainDisplayTime = plannedTime;
                this.crossedOutTime = '';
            }
            return;
        }
        this.mainDisplayTime = prognosisTime ?? plannedTime;
    }

    private getFormattedScheduledTime(): string {
        const scheduledInstant =
            typeof this.time.scheduledDeparture === 'string'
                ? Temporal.Instant.from(this.time.scheduledDeparture)
                : Temporal.Instant.fromEpochSeconds(this.time.scheduledDeparture);

        const scheduledTime = scheduledInstant.toZonedDateTimeISO(this.timeZone);

        const now = Temporal.Now.instant();
        const duration = now.until(scheduledTime.toInstant(), { largestUnit: 'minute' });
        const minutesToDeparture = duration.minutes;

        if (minutesToDeparture >= 0 && minutesToDeparture <= this.env.REALTIME_THRESHOLD_MINUTES) {
            if (minutesToDeparture === 0) {
                return this.translate.instant('EXPLORE_NOW_LABEL');
            }
            return `${minutesToDeparture} ${this.translate.instant('EXPLORER_SCHEDULE_MINUTE')}`;
        } else {
            return scheduledTime.toPlainTime().toString({ smallestUnit: 'minute' });
        }
    }

    private getFormattedDelayedDepartureTime(): string {
        const scheduledDeparture = this.time.scheduledDeparture;
        let inst =
            typeof scheduledDeparture === 'string'
                ? Temporal.Instant.from(scheduledDeparture)
                : Temporal.Instant.fromEpochSeconds(scheduledDeparture);

        const d = this.time.departureDelay;
        if (d != null) {
            inst = inst.add({ seconds: d });
        }
        const departureTime: Temporal.ZonedDateTime = inst.toZonedDateTimeISO(this.timeZone).round('minute');

        const now = Temporal.Now.instant();
        const duration = now.until(departureTime.toInstant(), { largestUnit: 'minute' });
        const minutesToDeparture = duration.minutes;

        if (minutesToDeparture >= 0 && minutesToDeparture <= this.env.REALTIME_THRESHOLD_MINUTES) {
            if (minutesToDeparture === 0) {
                return this.translate.instant('EXPLORE_NOW_LABEL');
            }
            return `${minutesToDeparture} ${this.translate.instant('EXPLORER_SCHEDULE_MINUTE')}`;
        } else {
            return departureTime.toPlainTime().toString({ smallestUnit: 'minute' });
        }
    }

    private getFormattedDepartureInstant(departureInstant: Temporal.ZonedDateTime): string {
        return this.datePipe.transform(departureInstant.epochMilliseconds, this.TIME_FORMAT, this.timeZoneOffset);
    }
}
